import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import MediaPicker from './MediaPicker';

interface ScheduledPost {
  id: string;
  message?: string;
  full_picture?: string;
  scheduled_publish_time?: number;
  permalink_url?: string;
}

function fmt(unixSeconds: number) {
  return new Date(unixSeconds * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// datetime-local wants local time with no timezone/seconds, e.g. "2026-11-03T14:30".
function toDatetimeLocal(unixSeconds: number) {
  const d = new Date(unixSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Props {
  botId: string;
}

export default function FacebookPostsPanel({ botId }: Props) {
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [posting, setPosting] = useState(false);

  const [scheduled, setScheduled] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingHadImage, setEditingHadImage] = useState(false);

  const loadScheduled = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-posts/scheduled`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not load scheduled posts');
      setScheduled(data.posts || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load scheduled posts');
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => { loadScheduled(); }, [loadScheduled]);

  function resetForm() {
    setMessage(''); setLink(''); setImageUrl(''); setScheduledFor('');
    setEditingId(null); setEditingHadImage(false);
  }

  function startEdit(post: ScheduledPost) {
    setEditingId(post.id);
    setEditingHadImage(!!post.full_picture);
    setMessage(post.message || '');
    setLink('');
    setImageUrl('');
    setScheduledFor(post.scheduled_publish_time ? toDatetimeLocal(post.scheduled_publish_time) : '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submitPost() {
    if (!message.trim() && !imageUrl) { alert('Write a message or add an image first.'); return; }
    setPosting(true);
    try {
      const res = editingId
        ? await apiFetch(`/api/bots/${botId}/facebook-posts/${editingId}`, {
            method: 'PUT',
            body: JSON.stringify({
              message: message.trim(),
              link: link.trim() || undefined,
              scheduledFor: scheduledFor || undefined,
            }),
          })
        : await apiFetch(`/api/bots/${botId}/facebook-posts`, {
            method: 'POST',
            body: JSON.stringify({
              message: message.trim(),
              link: link.trim() || undefined,
              imageUrl: imageUrl || undefined,
              scheduledFor: scheduledFor || undefined,
            }),
          });
      const data = await res.json();
      if (!data.success) {
        alert(data.friendly || data.error || 'Could not post. Please try again.');
        return;
      }
      const wasEditing = !!editingId;
      resetForm();
      if (wasEditing || data.scheduled) loadScheduled();
      if (!wasEditing && !data.scheduled) alert('Posted to your Page!');
    } catch (e) {
      alert('Could not post — check your connection and try again.');
    } finally {
      setPosting(false);
    }
  }

  async function cancelPost(postId: string) {
    if (!window.confirm('Cancel this scheduled post?')) return;
    setCancelling(postId);
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-posts/${postId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) { alert(data.friendly || data.error || 'Could not cancel.'); return; }
      setScheduled(prev => prev.filter(p => p.id !== postId));
      if (editingId === postId) resetForm();
    } catch (e) {
      alert('Could not cancel — check your connection and try again.');
    } finally {
      setCancelling(null);
    }
  }

  // Meta requires scheduled posts to be 10 minutes to 30 days out - the
  // min attribute gives the picker a sane floor, actual enforcement happens
  // server-side against the real current time.
  const minSchedule = new Date(Date.now() + 11 * 60 * 1000).toISOString().slice(0, 16);
  const maxSchedule = new Date(Date.now() + 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: editingId ? '1px solid #5B4FE8' : '1px solid var(--card-border)', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '600' }}>
            {editingId ? 'Editing scheduled post' : 'Post to your Page'}
          </div>
          {editingId && (
            <button
              onClick={resetForm}
              style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Cancel edit
            </button>
          )}
        </div>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={4}
          placeholder="What do you want to share?"
          style={{ width: '100%', padding: '12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '12px' }}
        />
        {editingHadImage ? (
          <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginBottom: '12px' }}>
            This post has an image — the image itself can't be edited here. Cancel and recreate the post to change it.
          </div>
        ) : (
          <div style={{ marginBottom: '12px' }}>
            <MediaPicker value={imageUrl} onChange={setImageUrl} kind="image" placeholder="Image URL (optional)" />
          </div>
        )}
        <input
          value={link}
          onChange={e => setLink(e.target.value)}
          placeholder="Link to share (optional, ignored if an image is attached)"
          style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }}
        />
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            Schedule for later (optional)
          </label>
          <input
            type="datetime-local"
            value={scheduledFor}
            min={minSchedule}
            max={maxSchedule}
            onChange={e => setScheduledFor(e.target.value)}
            style={{ padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' }}
          />
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>
            Leave blank to post immediately. Meta allows scheduling 10 minutes to 30 days ahead.
          </div>
        </div>
        <button
          onClick={submitPost}
          disabled={posting}
          style={{ background: '#5B4FE8', border: 'none', color: 'white', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: posting ? 'not-allowed' : 'pointer', opacity: posting ? 0.7 : 1 }}
        >
          {posting ? 'Saving…' : editingId ? 'Save changes' : scheduledFor ? 'Schedule post' : 'Post now'}
        </button>
      </div>

      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '24px' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
          Scheduled posts
        </div>
        {loading ? (
          <div style={{ color: 'var(--text-faint)', fontSize: '13px' }}>Loading…</div>
        ) : error ? (
          <div style={{ color: '#ef4444', fontSize: '13px' }}>{error}</div>
        ) : scheduled.length === 0 ? (
          <div style={{ color: 'var(--text-faint)', fontSize: '13px' }}>No posts scheduled right now.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {scheduled.map(post => (
              <div key={post.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px', background: 'var(--bg-page)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                {post.full_picture && (
                  <img src={post.full_picture} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '13px', marginBottom: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {post.message || <em style={{ color: 'var(--text-faint)' }}>(image post, no caption)</em>}
                  </div>
                  {post.scheduled_publish_time && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                      Scheduled for {fmt(post.scheduled_publish_time)}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button
                    onClick={() => startEdit(post)}
                    disabled={cancelling === post.id}
                    style={{ background: '#5B4FE820', color: '#5B4FE8', border: '1px solid #5B4FE840', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => cancelPost(post.id)}
                    disabled={cancelling === post.id}
                    style={{ background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    {cancelling === post.id ? '…' : 'Cancel'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
